import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface SearchUser {
  user_id: string;
  full_name: string | null;
  avatar_url: string | null;
  preferred_sports: string[] | null;
  skill_level: string | null;
  location: string | null;
  isFriend: boolean;
  hasPendingRequest: boolean;
}

export function useUserSearch() {
  const { user } = useAuth();
  const [users, setUsers] = useState<SearchUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchUsers = async (query: string, sport?: string) => {
    if (!user || query.length < 2) {
      setUsers([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let supabaseQuery = supabase
        .from('profiles')
        .select('user_id, full_name, avatar_url, preferred_sports, skill_level, location')
        .neq('user_id', user.id);

      // Search by name only (email no longer exposed)
      if (query) {
        supabaseQuery = supabaseQuery.ilike('full_name', `%${query}%`);
      }

      // Filter by sport if specified
      if (sport) {
        supabaseQuery = supabaseQuery.contains('preferred_sports', [sport]);
      }

      const { data: searchResults, error: searchError } = await supabaseQuery.limit(20);

      if (searchError) throw searchError;

      // Get current user's friends and pending requests
      const { data: friendsData } = await supabase
        .from('friends')
        .select('friend_id, user_id, status')
        .or(`user_id.eq.${user.id},friend_id.eq.${user.id}`);

      const friends = new Set();
      const pendingRequests = new Set();

      friendsData?.forEach(friendship => {
        const otherId = friendship.user_id === user.id ? friendship.friend_id : friendship.user_id;
        if (friendship.status === 'accepted') {
          friends.add(otherId);
        } else if (friendship.status === 'pending') {
          pendingRequests.add(otherId);
        }
      });

      const usersWithFriendStatus = searchResults?.map(profile => ({
        ...profile,
        isFriend: friends.has(profile.user_id),
        hasPendingRequest: pendingRequests.has(profile.user_id)
      })) || [];

      setUsers(usersWithFriendStatus);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to search users');
    } finally {
      setLoading(false);
    }
  };

  return {
    users,
    loading,
    error,
    searchUsers
  };
}